import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, formatApiError } from "@/lib/api";

const counsel = axios.create({ baseURL: API, withCredentials: true, timeout: 30000 });

export default function LegalPortal() {
  const [session, setSession] = useState(null);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [docs, setDocs] = useState([]);
  const [query, setQuery] = useState("");
  const [business,setBusiness]=useState('all');
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async () => { const { data } = await counsel.get("/legal/documents"); setDocs(data); };
  useEffect(() => {
    counsel.get("/legal/session").then(async ({ data }) => { setSession(data); await load(); })
      .catch((e) => { if (e.response?.status !== 401) setError("The lawyer portal is temporarily unavailable."); })
      .finally(() => setChecking(false));
  }, []);
  const login = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try { const { data } = await counsel.post("/legal/session", { email, password });
      setPassword(""); setSession(data); await load(); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const logout = async () => {
    try { await counsel.post("/legal/logout", {}, { headers: { "X-CSRF-Token": session.csrf_token } });
      setSession(null); setDocs([]); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };
  const download = async (doc, group=null) => {
    setError(""); setBusy(true);
    try { const { data } = await counsel.get(group?`/legal/document-groups/${encodeURIComponent(group)}/download`:`/legal/documents/${encodeURIComponent(doc.file_id)}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(data); const a = document.createElement("a");
      a.href = url; a.download = group?`${doc.document_title||'document'}.zip`:doc.original_filename; a.click(); setTimeout(() => URL.revokeObjectURL(url), 1000); }
    catch (err) { setError(err.response?.status === 401 ? "Your session expired. Sign in again." : "This document is unavailable or has not passed its malware scan.");
      if (err.response?.status === 401) { setSession(null); setDocs([]); } }
    finally { setBusy(false); }
  };
  const groups = {};
  const available=new Set(docs.map(d=>d.file_id));
  const businesses=[...new Map(docs.map(d=>[d.business_entity_id||'unassigned',d.business_entity_name||'Business records'])).entries()];
  docs.filter(d=>!available.has(d.duplicate_of_file_id))
    .forEach((d) => { const key = d.document_group_id || d.file_id; (groups[key] ||= []).push(d); });
  Object.values(groups).forEach(rows => rows.sort((a, b) => (a.page_number || 1) - (b.page_number || 1)));
  const visibleGroups=Object.entries(groups).filter(([,rows])=>rows.some(d=>(business==='all'||d.business_entity_id===business)&&`${d.document_title||''} ${d.original_filename} ${d.note||''} ${d.matter} ${d.category} ${d.business_entity_name||''} ${d.document_type_label||''}`.toLowerCase().includes(query.toLowerCase())));
  const input = "block w-full rounded-xl border border-[#b48b3f] bg-transparent p-3 my-3";
  return <main className="min-h-screen bg-black text-[#efe9da] px-6 py-12">
    <div className="max-w-4xl mx-auto">
      <a href="/">Jennae Michelle Holdings</a>
      <h1 className="text-3xl font-serif mt-8">Legal Document Room</h1>
      <p className="mt-3">Counsel may review the records the Founder has expressly released for legal examination. Access is read-only, and the system records sign-ins and document retrieval.</p>
      {error && <p role="alert" className="my-4 text-red-300">{error}</p>}
      {checking ? <p className="mt-6">Checking session…</p> : !session ?
        <form onSubmit={login} className="max-w-md mt-6">
          <label>Email<input className={input} type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} /></label>
          <label>Password<input className={input} type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} /></label>
          <button disabled={busy} className="jmh-cta rounded-xl">{busy ? "Signing in…" : "Enter Legal Document Room"}</button>
        </form> : <>
          <div className="flex justify-between mt-6"><span>{session.email}</span><button onClick={logout}>Sign out</button></div>
          <label>Search documents, matters and categories<input className={input} value={query} onChange={(e) => setQuery(e.target.value)} /></label>
          <label>Business<select className={input} value={business} onChange={e=>setBusiness(e.target.value)}><option className="bg-black" value="all">All released businesses</option>{businesses.map(([id,name])=><option className="bg-black" key={id} value={id}>{name}</option>)}</select></label>
          {!docs.length && <p className="mt-6">No documents have been released to counsel yet.</p>}
          {docs.length > 0 && !visibleGroups.length && <p>No matching documents.</p>}
          {visibleGroups.map(([group, rows]) => <section key={group} className="mt-8">
            <h2 className="text-xl font-serif">{rows[0].matter} · {rows[0].category} · {rows[0].document_title || rows[0].original_filename}</h2>
            <p className="mt-2">{rows[0].business_entity_name||'Business records'} · {rows.length} released pages</p>
            <button className="jmh-cta rounded-xl mt-3" disabled={busy||rows.some(d=>d.malware_state!=='SCAN_PASSED')} onClick={()=>download(rows[0],group)}>Download complete released document</button>
            {rows.map((d) => <div key={d.file_id} className="border-b border-[#b48b3f] py-4">
              <p>{d.document_title || d.original_filename} · Page {d.page_number || 1}</p><p className="text-sm opacity-70">{d.original_filename}</p><p className="text-sm opacity-70">{d.created_at?.slice(0, 10)} · {d.malware_label}</p>
              {d.note && <p className="text-sm mt-2">{d.note}</p>}
              <button className="mt-2 underline disabled:opacity-40" disabled={busy || d.malware_state !== "SCAN_PASSED"} onClick={() => download(d)}>Download</button>
            </div>)}
          </section>)}
        </>}
    </div>
  </main>;
}
